import QRCode from "qrcode";
import { DEFAULT_QR_THEME } from "./allocateQrThemes";

const wrapCanvasText = (ctx, text, maxWidth) => {
  const words = String(text || "")
    .split(/\s+/)
    .filter(Boolean);
  const lines = [];
  let current = "";
  words.forEach((word) => {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width <= maxWidth) {
      current = test;
    } else {
      if (current) lines.push(current);
      current = word;
    }
  });
  if (current) lines.push(current);
  return lines.length ? lines : [""];
};

const roundRectPath = (ctx, x, y, w, h, r) => {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
};

export const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

/** Same event-pass image used on Allocate Guest preview / download. */
export const generateEventPassBlob = async ({
  payload,
  personName,
  eventName,
  occasionName = "",
  theme = DEFAULT_QR_THEME,
} = {}) => {
  const displayEvent = eventName || "Event Pass";
  const displayName = personName || "Guest";
  const width = 720;
  const height = 980;

  const qrDataUrl = await QRCode.toDataURL(String(payload || ""), {
    width: 440,
    margin: 2,
    errorCorrectionLevel: "H",
    color: { dark: "#1a2b2b", light: "#ffffff" },
  });

  const qrImg = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = qrDataUrl;
  });

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  ctx.fillStyle = theme.page || "#eef4f4";
  ctx.fillRect(0, 0, width, height);

  ctx.fillStyle = theme.primary || "#008080";
  ctx.fillRect(0, 0, width, 188);

  ctx.fillStyle = theme.chip || "#d7f1f1";
  ctx.font = "600 15px Helvetica, Arial, sans-serif";
  ctx.fillText("EVENT PASS", width / 2, 48);

  ctx.fillStyle = "#ffffff";
  ctx.font = "700 34px Georgia, 'Times New Roman', serif";
  const titleLines = wrapCanvasText(ctx, displayEvent, width - 96).slice(0, 3);
  const titleStartY = titleLines.length === 1 ? 112 : 96;
  titleLines.forEach((line, i) => {
    ctx.fillText(line, width / 2, titleStartY + i * 40);
  });

  ctx.fillStyle = theme.bar || "#cfe8e8";
  ctx.fillRect(width / 2 - 40, 168, 80, 3);

  const cardX = 64;
  const cardY = 214;
  const cardW = width - 128;
  const cardH = 560;

  ctx.save();
  ctx.shadowColor = "rgba(0, 64, 64, 0.14)";
  ctx.shadowBlur = 24;
  ctx.shadowOffsetY = 8;
  roundRectPath(ctx, cardX, cardY, cardW, cardH, 18);
  ctx.fillStyle = "#ffffff";
  ctx.fill();
  ctx.restore();

  ctx.strokeStyle = theme.bar || "#d7e6e6";
  ctx.lineWidth = 2;
  roundRectPath(ctx, cardX, cardY, cardW, cardH, 18);
  ctx.stroke();

  let contentY = cardY + 36;
  if (occasionName) {
    ctx.fillStyle = theme.primary || "#008080";
    ctx.font = "700 28px Georgia, 'Times New Roman', serif";
    const occasionLines = wrapCanvasText(ctx, occasionName, width - 160).slice(
      0,
      2
    );
    occasionLines.forEach((line, i) => {
      ctx.fillText(line, width / 2, contentY + i * 34);
    });
    contentY += occasionLines.length * 34 + 18;
  } else {
    contentY += 10;
  }

  const qrSize = 380;
  ctx.drawImage(qrImg, (width - qrSize) / 2, contentY, qrSize, qrSize);
  contentY += qrSize + 28;

  ctx.fillStyle = "#1f2a30";
  ctx.font = "700 30px Georgia, 'Times New Roman', serif";
  const nameLines = wrapCanvasText(ctx, displayName, width - 120).slice(0, 2);
  nameLines.forEach((line, i) => {
    ctx.fillText(line, width / 2, contentY + i * 36);
  });
  contentY += nameLines.length * 36 + 16;

  ctx.fillStyle = "#5b646c";
  ctx.font = "400 16px Helvetica, Arial, sans-serif";
  ctx.fillText("Please present this pass at the venue", width / 2, contentY);

  ctx.fillStyle = theme.primary || "#008080";
  ctx.fillRect(0, height - 40, width, 40);
  ctx.fillStyle = "#ffffff";
  ctx.font = "600 13px Helvetica, Arial, sans-serif";
  ctx.fillText("Festiva by Tecogis", width / 2, height - 20);

  return await new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Could not create event pass image"));
    }, "image/png");
  });
};
